import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, ChartRowDataArray } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { createRowData } from "./common";
import { ChartDialogConfig } from "../../common/constants";

export interface IGetSimulationResultsResponceModel {
  /**
   * シミュレーションの種類（step/impulse/compare）
   */
  type: string;
  /**
   * 比較用ファイル名
   */
  comparisonfile: string;
  /**
   * step間の経過時間[msec]
   */
  interval: number;
  /**
   * 比較用ファイルの入力チャネルのタグ名配列（例：["MV"]）
   */
  inputtag: string[];
  /**
   * 出力データのチャネル名の配列（例： [ "Sim001-PV Fit:80%" , "Sim002-PV Fit:60%", "reference" ]）
   */
  outputtags: string[];
  /**
   * 入力値の配列（1行配列の配列）
   */
  inrows: ChartRowDataArray;
  /**
   * 出力値の配列（1行配列の配列）
   */
  outrows: ChartRowDataArray;
}

function convertInData(data: IGetSimulationResultsResponceModel): ChartRowDataArray {
  // rows
  const datalist: ChartRowDataArray = new Array(data.inrows.length + 1);
  datalist[0] = ["x", ...data.inputtag];
  data.inrows.forEach((row, i) => {
    const idx = i + 1;
    const xdata = i * data.interval / 1000;
    datalist[idx] = [xdata, ...row];
  });
  return datalist;
}

function convertOutData(data: IGetSimulationResultsResponceModel): ChartRowDataArray {
  // rows
  const datalist: ChartRowDataArray = new Array(data.outrows.length + 1);
  datalist[0] = ["x", ...data.outputtags];
  data.outrows.forEach((row, i) => {
    const idx = i + 1;
    const xdata = i * data.interval / 1000;
    datalist[idx] = [xdata, ...row];
  });
  return datalist;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: { Authorization: "Bearer " + token },
    });
  } else {
    return axios.get(url);
  }
}

export interface ISimulationResultsRow extends IGetSimulationResultsResponceModel {
  /**
   * id
   */
  id: number;
  /**
   * データ数
   */
  simdatanum: number;  
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからシミュレータのシミュレーション結果の取得が成功した場合に呼び出されます
   * @callback success
   * @param {ISimulationResultsRow[]} rows - シミュレーション結果一覧
   * @param {number} status - httpステータス
   */
  success: (rows: ISimulationResultsRow[], status: number) => void;
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
  authService?: AuthService;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((values) => {
      const datas: IGetSimulationResultsResponceModel[] = values.data
      const rows:  ISimulationResultsRow[] = [];
      const threshold = ChartDialogConfig.RESULT_CHART_THRESHOLD;
      datas.forEach((row, i) => {
        const input = convertInData(row);
        const output = convertOutData(row);
        const datanum = input.length - 1;
        const comparisonfile = row.comparisonfile ? row.comparisonfile : "";
        
        rows.push({
          id: i,
          type: row.type,
          comparisonfile: comparisonfile,
          simdatanum: datanum,
          interval: row.interval,
          inputtag: row.inputtag,
          outputtags: row.outputtags,
          inrows: createRowData(input, threshold, true),
          outrows: createRowData(output, threshold, true)
        });
      });
      rows.sort(function(a, b) {
        if (a.type > b.type) {
          return -1;
        } else if (a.type < b.type) {
          return 1;
        } else {
          return 0;
        }
      });
      params.success(rows, values.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetSimulationResultsParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
}

/**
 * シミュレータのシミュレーション結果を取得します
 * @function getSimulationResults
 * @param {IGetSimulationResultsParams} params - シミュレーション結果取得パラメータ
 */
export function getSimulationResults(params: IGetSimulationResultsParams): void {
  const {
    authService,
    projectid,
  } = params;
  const url = WebAPI.GETRESULT_SIMULATOR(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getSimulatorResultsRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * シミュレータのシミュレーション結果を取得します
 * @function getSimulatorResultsRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function getSimulatorResultsRetry401(
  authService: AuthService,
  url: string,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}